import React, { useState, useEffect } from 'react';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { SelectUser } from '@Shared.Angular/flowingly.components/SelectUser/SelectUser';
import { getService } from '@Client/runner.hooks/useService';
import { IUserMaintenanceModel } from '@Shared.Angular/@types/core/contracts/queryModel/user/userMaintenanceModel';
import { IUserSummary } from '@Shared.Angular/@types/core/contracts/queryModel/user/userSummary';
import { Guid } from '@Shared.Angular/@types/guid';

type Props = {
  user: IUserSummary;
  onClose: (updated: boolean) => void;
};

export const MaintenanceUserDetailModal = ({
  user: initialUser,
  onClose
}: Props) => {
  const maintenanceService = getService('maintenanceService');
  const notificationService = getService('notificationService');
  const validationService = getService('validationService');

  const [hasFinishedLoading, setHasFinishedLoading] = useState(false);
  const [user, setUser] = useState(initialUser);
  const [selectedManagerId, setSelectedManagerId] = useState<Guid>(
    initialUser.managerUserId
  );
  const [changeRequestId, setChangeRequestId] = useState('');
  const [emailError, setEmailError] = useState('');

  const emailValidationPattern = validationService.getEmailValidationPattern();

  const isUserDeleted = user?.delete || false;

  const getUserSummaryById = async (
    userId: Guid,
    businessId: Guid
  ): Promise<IUserSummary | undefined> => {
    if (!userId || !businessId) {
      return undefined;
    }

    const filterParams = {
      filter: {
        filters: [{ field: 'id', value: userId }]
      }
    };

    const searchUserResult = await maintenanceService.getUsersWithOptions(
      filterParams,
      businessId
    );
    return searchUserResult.users[0];
  };

  useEffect(() => {
    const loadUser = async () => {
      const currentUserSummary = await getUserSummaryById(
        initialUser.id,
        initialUser.businessId
      );
      if (currentUserSummary) {
        setUser(currentUserSummary);
        setSelectedManagerId(currentUserSummary.managerUserId);
      }
      setHasFinishedLoading(true);
    };
    loadUser();
  }, [initialUser.id, initialUser.businessId]);

  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    if (!emailValidationPattern.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleUpdate = () => {
    if (!validateEmail(user.email)) {
      return;
    }

    const updateModel: IUserMaintenanceModel = {
      userId: user.id,
      businessId: user.businessId,
      managerId: selectedManagerId,
      inviteUser: user.inviteUser,
      delete: user.delete,
      email: user.email,
      changeRequestId: changeRequestId
    };

    maintenanceService.updateUser(updateModel).then(() => {
      notificationService.showSuccessToast('User Updated');
      onClose(true);
    });
  };

  const handleCancel = () => {
    onClose(false);
  };

  const handleInviteUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser({ ...user, inviteUser: e.target.checked });
  };

  const handleDeleteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser({ ...user, delete: e.target.checked });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setUser({ ...user, email: newEmail });
    setEmailError('');
  };

  const handleEmailBlur = () => {
    validateEmail(user.email);
  };

  const handleChangeRequestIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setChangeRequestId(e.target.value);
  };

  const handleManagerSelect = (userId: Guid) => {
    setSelectedManagerId(userId);
  };

  if (!hasFinishedLoading) {
    return null;
  }

  return (
    <Modal
      id="maintenance-user-detail-modal"
      title={`Edit User: ${user.fullName}`}
      size="md"
      onClose={handleCancel}
      onCancelClick={handleCancel}
      onActionClick={handleUpdate}
      actionButtonText="Save"
      actionButtonType="primary"
    >
      <div className="edit-user-modal content">
        <fieldset>
          <div className="row s12 mb-20">
            <label className="input-field col s6 pt-11 mt-0">Invited</label>
            <div className="input-field col s5 mt-0">
              <div className="switch">
                <label>
                  <input
                    id="inviteUser"
                    type="checkbox"
                    className="clickable"
                    checked={user.inviteUser}
                    onChange={handleInviteUserChange}
                  />
                  <span className="lever"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="row s12 mb-20">
            <label className="input-field col s6 pt-11 mt-0">Deleted</label>
            <div className="input-field col s5 mt-0">
              {isUserDeleted && (
                <div className="switch">
                  <label>
                    <input
                      id="deleteUser"
                      type="checkbox"
                      className="clickable"
                      checked={user.delete}
                      onChange={handleDeleteChange}
                    />
                    <span className="lever"></span>
                  </label>
                </div>
              )}
              {!isUserDeleted && (
                <span>
                  false{' '}
                  <i
                    className="fa-light fa-info-circle"
                    title="Users can only be deleted via the setup page"
                  ></i>
                </span>
              )}
            </div>
          </div>

          <div className="row pt-10">
            <label className="input-field col s6 pt-11 mt-0">Manager</label>
            <div className="input-field col s6 mt-0">
              <SelectUser
                businessId={user.businessId}
                selectedUserId={selectedManagerId}
                onUserSelect={handleManagerSelect}
              />
            </div>
          </div>

          <div className="row">
            <label htmlFor="email" className="active mt-25 pl-0 col s6">
              Email
            </label>
            <div className="input-field col s6">
              <input
                id="email"
                name="email"
                type="email"
                className="text-box mb-0"
                value={user.email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
              />
              {emailError && (
                <div className="errorMessage pt-10">{emailError}</div>
              )}
            </div>
          </div>

          <hr />

          <div className="row">
            <label
              htmlFor="changeRequestId"
              className="active mt-25 pl-0 col s6"
            >
              Change Request Id <span className="optional">(Optional)</span>
            </label>
            <div className="input-field col s6">
              <input
                id="changeRequestId"
                name="changeRequestId"
                type="text"
                className="text-box mb-0"
                autoComplete="off"
                value={changeRequestId}
                onChange={handleChangeRequestIdChange}
              />
            </div>
          </div>

          <label>Roles</label>
          <ul className="ml-20">
            {user.roles &&
              user.roles.map((role, index) => <li key={index}>{role.name}</li>)}
          </ul>
        </fieldset>
      </div>
    </Modal>
  );
};
